<template>
  <BaseCenteredModal
    v-model:is-modal-open="localIsModalOpen"
    :title="$t('button.edit_new_mv', { var: $t('global.item') })"
    size="medium"
  >
    <template #body>
      <div v-if="form" class="flex w-full flex-col gap-4 p-1">
        <BaseShowEditInput
          v-model="form.name"
          :label="$t('attributes.name')"
          name="name"
          :edition-mode="true"
          :errors="errors?.name"
          required
        />
        <BaseShowEditTextArea
          v-model="form.description"
          :label="$t('attributes.description')"
          name="description"
          :edition-mode="true"
          :errors="errors?.description"
          :rows="3"
        />

        <div class="grid grid-cols-3 gap-3">
          <BaseShowEditInput
            v-model="form.quantity"
            :label="$t('attributes.quantity')"
            name="quantity"
            type="number"
            parse-type="int"
            :min="0"
            :edition-mode="true"
            :errors="errors?.quantity"
          />
          <BaseShowEditInput
            v-model="form.vat_rate"
            :label="$t('attributes.vat_rate')"
            name="vat_rate"
            type="number"
            parse-type="float"
            :min="0"
            :max="100"
            append-string="%"
            :edition-mode="true"
            :errors="errors?.vat_rate"
          />
          <BaseShowEditInput
            v-model="form.reduction"
            :label="$t('attributes.reduction')"
            name="reduction"
            type="number"
            parse-type="float"
            :min="0"
            :max="100"
            append-string="%"
            :edition-mode="true"
            :errors="errors?.reduction"
          />
        </div>

        <div class="grid grid-cols-3 gap-3">
          <BaseShowEditPriceInput
            v-model="form.price_excluding_vat"
            :label="$t('attributes.price_excluding_vat')"
            name="price_excluding_vat"
            price-type="htva"
            :errors="errors?.price_excluding_vat"
          />
          <BaseShowEditPriceInput
            :model-value="vatAmount"
            :label="`${$t('attributes.vat')} (${form.vat_rate || 0}%)`"
            name="vat_amount"
            price-type="tva"
            disabled
          />
          <BaseShowEditPriceInput
            :model-value="priceIncludingVat"
            :label="$t('attributes.price_including_vat')"
            name="price_including_vat"
            price-type="tvac"
            @update:model-value="setFromIncludingVat"
          />
        </div>

        <BaseShowEditTextArea
          v-model="form.remarks"
          :label="$t('attributes.remarks')"
          name="remarks"
          :edition-mode="true"
          :errors="errors?.remarks"
          :rows="2"
        />
      </div>
    </template>
    <template #footer>
      <BaseButton
        :title="$t('button.save')"
        icon="paper-plane"
        color="primary"
        @click="submitForm"
      />
      <BaseButton :title="$t('button.close')" color="dark" @click="close" />
      <span class="flex-1"></span>
      <BaseButton
        :title="$t('button.delete_new_mv', { var: $t('global.item') })"
        icon="trash"
        color="danger"
        @click="deleteItem"
      />
    </template>
  </BaseCenteredModal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import BaseCenteredModal from '@c/addf-package/components/modal/BaseCenteredModal.vue'

const toNumber = (value) =>
  value === null || value === undefined || value === '' ? null : Number(value)
const round = (value) => Math.round(value * 100) / 100

/**
 * Edits an invoice line on FM's price inputs: the price excluding VAT is
 * stored, the price including VAT recomputes it.
 */
export default {
  name: 'InvoiceItemEditDialog',
  components: { BaseCenteredModal },
  props: {
    invoiceItemId: { type: [Number, String], required: false, default: null },
    isModalVisible: { type: Boolean, required: true }
  },
  emits: ['close', 'backdropClicked'],
  data() {
    return { form: null }
  },
  computed: {
    ...mapGetters({
      invoice_item: 'invoicing/getInvoiceItem',
      errors: 'auth/getErrors'
    }),
    localIsModalOpen: {
      get() {
        return this.isModalVisible
      },
      set(value) {
        if (!value) this.close()
      }
    },
    vatRate() {
      return toNumber(this.form?.vat_rate) ?? 0
    },
    vatAmount() {
      const price = toNumber(this.form?.price_excluding_vat)
      return price === null ? null : round((price * this.vatRate) / 100)
    },
    priceIncludingVat() {
      const price = toNumber(this.form?.price_excluding_vat)
      return price === null ? null : round(price * (1 + this.vatRate / 100))
    }
  },
  watch: {
    invoiceItemId: {
      immediate: true,
      handler(id) {
        if (id) this.fetchInvoiceItemID({ id })
      }
    },
    invoice_item: {
      immediate: true,
      handler(item) {
        this.form = item ? { ...item } : null
      }
    },
    // Reopening the same line drops the edits that were not saved.
    isModalVisible(open) {
      if (open) this.form = this.invoice_item ? { ...this.invoice_item } : null
    }
  },
  methods: {
    ...mapActions({
      fetchInvoiceItemID: 'invoicing/fetchInvoiceItemID',
      putInvoiceItem: 'invoicing/updateInvoiceItem',
      deleteInvoiceItem: 'invoicing/deleteInvoiceItem',
      undoDeleteInvoiceItem: 'invoicing/undoDeleteInvoiceItem'
    }),
    close() {
      this.$emit('close')
    },
    setFromIncludingVat(value) {
      const price = toNumber(value)
      this.form.price_excluding_vat =
        price === null ? null : round(price / (1 + this.vatRate / 100))
    },
    submitForm() {
      this.putInvoiceItem({
        id: this.invoiceItemId,
        invoice_item: this.form
      }).then(this.close)
    },
    deleteItem() {
      const id = this.invoiceItemId
      this.deleteInvoiceItem({
        id,
        invoice_item: this.form
      }).then(() => {
        this.$h.toastAction(
          'delete',
          this.$t('toasts.deleted_m', { var: this.$t('global.item') }),
          null,
          [
            {
              title: this.$t('button.cancel'),
              class: 'text-red-700',
              action: () => this.undoDelete(id)
            }
          ]
        )
        this.close()
      })
    },
    undoDelete(id) {
      this.undoDeleteInvoiceItem({ id }).then(() => {
        this.$h.toastSuccess(
          this.$t('toasts.reseted_m', { var: this.$t('global.item') })
        )
      })
    }
  }
}
</script>
